import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { HiExclamation } from 'react-icons/hi'

import {
  Box,
  Flex,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
} from '@chakra-ui/react'

import {
  Alert,
  Box as DarkBox,
  Modal as DarkModal,
  Text as DarkText,
} from '@hivewatch/dark'

import { Share } from '@/components/icons'
import { Modal } from '@/components/overlays'
import { Button } from '@/components/ui'
import { IncidentActionType } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  incidentName: string
  currentOwner: string
  displayId: string
  isLoading: boolean
  nextAction:
    | IncidentActionType.AcknowledgementByOperator
    | IncidentActionType.Resolved
}

const getIncidentActionDisplayName = (
  action: IncidentActionType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>
) => {
  switch (action) {
    case IncidentActionType.AcknowledgementByOperator:
      return t('incidents:acknowledge')
    case IncidentActionType.Resolved:
      return t('incidents:incident_drawer_resolve_header')
  }
}

export const TakeOverIncidentModal = ({
  isOpen,
  onClose,
  onSubmit,
  incidentName,
  currentOwner,
  displayId,
  isLoading,
  nextAction,
}: IProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentActions } = usePermissions()
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const colorScheme = useLightMode()

  if (isRebrand) {
    const actionName = getIncidentActionDisplayName(nextAction, t)
    return (
      <DarkModal
        bodyStyleOverrides={{ pb: '8px' }}
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveDestructive
        isSaveDisabled={!shouldEnableIncidentActions}
        isSaveLoading={isLoading}
        onClose={onClose}
        onSave={onSubmit}
        saveButtonLabel={tVar(t, 'incidents:incident_drawer_take_over_button', {
          action: actionName,
        })}
        showCancelButton
        size='compact'
        title={t('incidents:incident_drawer_take_over_title')}
        zIndex={1500}
      >
        <DarkText
          color={
            colorScheme === 'light' ? 'light.text.secondary' : 'text.secondary'
          }
          textStyle='caption'
        >
          {t('incidents:incident_drawer_release_incident_name_label')}
        </DarkText>
        <DarkText
          color={
            colorScheme === 'light' ? 'light.text.primary' : 'text.primary'
          }
          data-testid='TakeOverIncidentModal:incidentDisplayName'
          mt='4px'
          textStyle='heading'
        >
          {`${displayId} ${incidentName}`}
        </DarkText>
        <DarkBox mt='16px'>
          <Alert
            colorScheme={colorScheme}
            description={tVar(
              t,
              'incidents:incident_drawer_take_over_warning',
              {
                owner: currentOwner,
                action: actionName,
              }
            )}
            title={t('common:warning_title')}
            variant='error'
          />
        </DarkBox>
      </DarkModal>
    )
  }

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff' overflow='hidden'>
        <ModalCloseButton color='#000' opacity={0.4} />

        <>
          <ModalBody p='0'>
            <Box px='10' py='6'>
              <Box
                color='#353849'
                fontSize='26px'
                fontWeight='extrabold'
                letterSpacing='-0.4px'
                lineHeight='1'
              >
                Take Over Incident
              </Box>
              <Box
                color='#8c8d97'
                fontSize='17px'
                letterSpacing='-0.4px'
                mt='6'
              >
                Incident Name:
              </Box>
              <Box
                color='#353849'
                data-testid='TakeOverIncidentModal:incidentDisplayName'
                fontSize='26px'
                fontWeight='extrabold'
                letterSpacing='-0.4px'
              >
                {`${displayId} ${incidentName}`}
              </Box>
            </Box>
          </ModalBody>
          <Box px='4'>
            <Flex
              alignItems='center'
              color='#D11D3A'
              flexDirection='row'
              pb='5'
            >
              <HiExclamation size='24px' />
              <Box
                data-testid='TakeOverIncidentModal:message'
                fontSize='16px'
                fontWeight='medium'
                letterSpacing='-0.53px'
                ml='1'
              >
                Current owner is{' '}
                <Box as='span' fontWeight='bold'>
                  {currentOwner}
                </Box>
                . Are you sure you would like to{' '}
                <Box as='span' fontWeight='bold'>
                  Take Over and {getIncidentActionDisplayName(nextAction, t)}
                </Box>{' '}
                this Incident?
              </Box>
            </Flex>
            <ModalFooter px='0'>
              <Button mr={3} onClick={onClose} variant='secondary'>
                Cancel
              </Button>
              <Button
                isDisabled={!shouldEnableIncidentActions}
                isLoading={isLoading}
                leftIcon={<Icon as={Share} boxSize={6} strokeColor='#fff' />}
                loadingText='Taking Over...'
                onClick={onSubmit}
                variant='danger'
              >
                {`Take Over & ${getIncidentActionDisplayName(nextAction, t)}`}
              </Button>
            </ModalFooter>
          </Box>
        </>
      </ModalContent>
    </Modal>
  )
}
